import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Menu, X, User, LogOut, Settings, ShoppingBag } from 'lucide-react';
import logoDsrs from '../../assets/logos/logodsrs.png';
import logoDsrsWhite from '../../assets/logos/logodsrs.white.png';

const PLANITY_URL = process.env.REACT_APP_PLANITY_URL || 'https://www.planity.com/madame-dsrs-95570-attainville';

const navLinks = [
  { path: '/', label: 'Accueil' },
  { path: '/onglerie-pedicure', label: 'Onglerie & Pédicure' },
  { path: '/bien-etre', label: 'Bien-être' },
  { path: '/press-on-nails', label: 'Press On Nails' },
  { path: '/formation', label: 'Formation' },
  { path: '/atelier-evenementiel', label: 'Atelier B2B' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const { getItemCount } = useCart();
  const cartCount = getItemCount();
  const headerTextClass = isScrolled
    ? 'text-[var(--charcoal)] hover:text-[var(--gold)]'
    : 'text-white hover:text-white/80';
  const headerMutedTextClass = isScrolled
    ? 'text-[var(--charcoal)]/60 hover:text-[var(--charcoal)]'
    : 'text-white/70 hover:text-white';
  const desktopNavTextClass = isScrolled
    ? 'text-[var(--charcoal)] hover:text-[var(--dark-rosegold)]'
    : 'text-white hover:text-white/80';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'glass shadow-lg py-3'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className="relative flex items-center flex-shrink-0 paddingright-4">
          <span className="relative block h-10 md:h-12 scale-125 origin-left">
            <img
              src={logoDsrs}
              alt="Madame DSRS"
              className={`h-full w-auto transition-opacity duration-500 ${
                isScrolled ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <img
              src={logoDsrsWhite}
              alt=""
              aria-hidden="true"
              className={`absolute left-0 top-0 h-full w-auto transition-opacity duration-500 ${
                isScrolled ? 'opacity-0' : 'opacity-100'
              }`}
            />
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden xl:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              className={`relative text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-300 ${desktopNavTextClass}`}
            >
              {link.label}
              {location.pathname === link.path && (
                <motion.div
                  layoutId="nav-underline"
                  className="absolute -bottom-1 left-0 right-0 h-[1px] bg-[var(--gold)]"
                />
              )}
            </Link>
          ))}
        </nav>

        {/* Right Side Actions */}
        <div className="hidden xl:flex items-center gap-4">
          {/* Cart Icon */}
          <Link
            to="/panier"
            data-testid="nav-cart"
            className={`relative p-2 transition-colors ${headerTextClass}`}
            aria-label="Panier"
          >
            <ShoppingBag size={20} />
            {cartCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--gold)] text-white text-xs rounded-full flex items-center justify-center font-medium"
              >
                {cartCount}
              </motion.span>
            )}
          </Link>

          {/* Auth Links */}
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              {/* Admin Link */}
              {isAdmin && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium transition-colors flex items-center gap-2 ${headerTextClass}`}
                >
                  <Settings size={14} />
                  Admin
                </Link>
              )}
              
              {/* Mon Compte */}
              <Link
                to="/mon-compte"
                data-testid="nav-mon-compte"
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium transition-colors flex items-center gap-2 ${headerTextClass}`}
              >
                <User size={14} />
                {user?.first_name}
              </Link>
              
              {/* Logout Button */}
              <button
                onClick={logout}
                data-testid="nav-logout"
                className={`p-2 transition-colors ${headerMutedTextClass}`}
                aria-label="Se déconnecter"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium transition-colors flex items-center gap-2 ${headerTextClass}`}
            >
              <User size={14} />
              Connexion
            </Link>
          )}

          {/* CTA Button */}
          <a
            href={PLANITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-planity"
            className="px-6 py-3 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300"
          >
            Prendre RDV
          </a>
        </div>

        {/* Mobile: Cart + Menu Button */}
        <div className="flex items-center gap-2 xl:hidden">
          {/* Mobile Cart */}
          <Link
            to="/panier"
            data-testid="mobile-nav-cart"
            className={`relative p-2 transition-colors ${headerTextClass}`}
            aria-label="Panier"
          >
            <ShoppingBag size={22} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--gold)] text-white text-xs rounded-full flex items-center justify-center font-medium">
                {cartCount}
              </span>
            )}
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`p-2 transition-colors ${headerTextClass}`}
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden glass border-t border-[var(--champagne)]"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col py-6 px-6 gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`text-sm uppercase tracking-[0.15em] py-2 transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[var(--dark-rosegold)]'
                      : 'text-[var(--charcoal)]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Cart Link Mobile */}
              <Link
                to="/panier"
                className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] py-2 text-[var(--charcoal)]"
              >
                <ShoppingBag size={16} />
                Panier {cartCount > 0 && `(${cartCount})`}
              </Link>
              
              {/* Auth Links Mobile */}
              <div className="border-t border-[var(--champagne)] pt-4 mt-2">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/mon-compte"
                      data-testid="mobile-nav-compte"
                      className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] py-2 text-[var(--charcoal)]"
                    >
                      <User size={16} />
                      Mon Compte ({user?.first_name})
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        data-testid="mobile-nav-admin"
                        className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] py-2 text-[var(--charcoal)]"
                      >
                        <Settings size={16} />
                        Administration
                      </Link>
                    )}
                    <button
                      onClick={logout}
                      data-testid="mobile-nav-logout"
                      className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] py-2 text-[var(--charcoal)]/60"
                    >
                      <LogOut size={16} />
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    data-testid="mobile-nav-login"
                    className="flex items-center gap-2 text-sm uppercase tracking-[0.15em] py-2 text-[var(--charcoal)]"
                  >
                    <User size={16} />
                    Connexion
                  </Link>
                )}
              </div>
              
              <a
                href={PLANITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-btn-planity"
                className="mt-4 px-6 py-3 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium text-center hover:bg-[var(--dark-gold)] transition-colors duration-300"
              >
                Prendre RDV
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
